import { assess, categoryOf, evalSupportedVendors } from './index'
import { getCredential, maxDurationMsFor } from './credentials'
import { SpeechError, SpeechErrorCode, toSpeechError } from './errors'
import { cleanRefText, uuid } from './utils'
import SpeechRecorder from './recorder'
import { reportEvaluation } from './report'
import { uploadRecording } from './upload'

/**
 * 一次朗读评测的完整编排：录音 → 停止 → 送厂商测评 → 上传录音 → 上报结果。
 *
 * 与被替换掉的讯飞实现的关键差异：厂商侧是"整段音频一次性 HTTP 请求"，
 * 不再有分片上行、断线重连和帧重发，所以状态机简单很多；"重来"也只是拿同一段
 * 录音再送一次，不需要用户重新读。
 *
 * 回调：
 * - record_begin_callback()：录音真正开始（麦克风与凭证都就绪）
 * - evaluate_callback({ evaluation, mp3Url, xmlUrl, durationMs, clientRecordId })
 * - error_callback(err, phase)：phase 为 init / start / processing，UI 按阶段决定提示方式
 * - wave_callback(Uint8Array)：录音中的时域波形，50ms 一次
 */
export default class SpeechSession {
  constructor({
    wave_callback,
    record_begin_callback,
    evaluate_callback,
    error_callback,
  } = {}) {
    this.wave_callback = wave_callback || null
    this.record_begin_callback = record_begin_callback || null
    this.evaluate_callback = evaluate_callback || null
    this.error_callback = error_callback || null

    this.recorder = new SpeechRecorder({ wave_callback: this.wave_callback })

    /** idle / ready / recording / processing / disabled / destroy */
    this.status = 'idle'
    this.refText = ''
    this.category = 'sentence'
    /** 本轮录音的时长上限（毫秒），取决于后端下发的厂商；凭证预取前是默认值 */
    this.maxDurationMs = 60000
    this.credentialResolved = false
    this.credentialKey = ''
    this.destroyed = false

    this.lastWav = null
    this.lastDurationMs = 0
    this.playingUrl = ''

    // 上报上下文，由调用方在 record 前用 setBizContext 设置
    this.bizContext = {}
  }

  /** 上报用的业务上下文：bizSource / bizId / questionId / attempt */
  setBizContext(context = {}) {
    this.bizContext = { ...this.bizContext, ...context }
  }

  /**
   * 申请麦克风并预取凭证。凭证在这里取一次是为了拿到 vendor，
   * 从而知道本轮录音的时长上限（Azure 30 秒，SpeechSuper 60 秒）。
   * 麦克风拿不到才 reject；凭证预取失败只记 warn，留到点录音时再试一次。
   */
  async init() {
    if (this.destroyed) return
    try {
      await this.recorder.init()
    } catch (e) {
      this.status = 'disabled'
      const err = toSpeechError(e)
      this.__emitError(err, 'init')
      throw err
    }
    // 等授权这段时间里组件可能已经卸载
    if (this.destroyed) return
    if (this.status !== 'recording' && this.status !== 'processing') this.status = 'ready'
    try {
      await this.__prefetchCredential()
    } catch (e) {
      console.warn('[speech] 预取凭证失败，点录音时再试', e)
    }
  }

  /**
   * 开始录音。resolve 时录音已经在进行（record_begin_callback 已触发）；
   * 失败走 error_callback(err, 'start') 并 resolve(false)。
   *
   * @param {string} category 题目的 evaluate.category（read_word / read_sentence / read_chapter …）
   * @param {string} text 题目的 evaluate.evaluate_text
   */
  async record({ category, text } = {}) {
    if (this.destroyed) return false
    if (this.status === 'recording' || this.status === 'processing') return false

    try {
      // 麦克风已经确定拿不到时也要走 error_callback，否则调用方的按钮会停在"准备录音"
      if (this.status === 'disabled') {
        throw new SpeechError(SpeechErrorCode.MIC_DENIED, '录音设备不可用')
      }
      if (!this.recorder.ready) await this.recorder.init()
      // 凭证签不出来就不要让用户白读一遍，这里直接报错
      await this.__prefetchCredential()
      // await 期间用户可能又点了一次或者组件已卸载，恢复后必须重查状态
      if (this.destroyed || this.status === 'recording' || this.status === 'processing') return false

      // 参考文本必须先洗掉编辑标记，否则厂商会把标记当成要朗读的内容
      this.refText = cleanRefText(text)
      this.category = categoryOf(category, this.refText)
      this.lastWav = null
      this.recorder.start()
      this.status = 'recording'
    } catch (e) {
      const err = toSpeechError(e)
      if (err.code === SpeechErrorCode.MIC_DENIED || err.code === SpeechErrorCode.NO_GET_USER_MEDIA) {
        this.status = 'disabled'
      }
      this.__emitError(err, 'start')
      return false
    }
    // 回调放在 try 外面：它是调用方的 UI 逻辑，里面抛错不该被当成"开始录音失败"
    this.record_begin_callback && this.record_begin_callback()
    return true
  }

  /**
   * 结束录音并送评。结果通过 evaluate_callback 回调；失败走 error_callback(err, 'processing')。
   * 录音时长上限由 UI 的倒计时保证（倒计时到 0 会调到这里），本类不再另起定时器。
   */
  async stop() {
    if (this.status !== 'recording') return null
    const durationMs = this.recorder.stop()
    this.status = 'processing'

    const wav = this.recorder.exportWav()
    if (!wav) {
      this.status = 'ready'
      // 让位一个微任务：调用方 stop() 之后往往紧跟着同步的按钮状态切换，
      // 不让位的话这里的错误回调会被它覆盖
      await Promise.resolve()
      this.__emitError(
        toSpeechError(new Error('没有录到音频'), SpeechErrorCode.EMPTY_AUDIO),
        'processing'
      )
      return null
    }
    this.lastWav = wav
    this.lastDurationMs = durationMs
    return this.__process(wav, durationMs)
  }

  /** "重来"：拿上一段录音再送一次评测，不需要重新录 */
  async retry() {
    if (this.destroyed || this.status === 'recording' || this.status === 'processing') return null
    if (!this.lastWav) {
      // 没有可重送的录音时也要给一个收尾信号，否则调用方的 UI 会停在"评测中"
      await Promise.resolve()
      this.__emitError(
        toSpeechError(new Error('没有可重新评测的录音'), SpeechErrorCode.EMPTY_AUDIO),
        'processing'
      )
      return null
    }
    this.status = 'processing'
    return this.__process(this.lastWav, this.lastDurationMs)
  }

  /** 放弃本次录音，不发任何请求 */
  cancel() {
    if (this.status === 'recording') this.recorder.stop()
    if (this.status !== 'disabled') this.status = this.recorder.ready ? 'ready' : 'idle'
  }

  /** 本地回放地址（不依赖上传结果） */
  createSrc() {
    if (this.playingUrl) {
      URL.revokeObjectURL(this.playingUrl)
      this.playingUrl = ''
    }
    if (!this.lastWav) return ''
    this.playingUrl = URL.createObjectURL(this.lastWav)
    return this.playingUrl
  }

  destroy() {
    this.destroyed = true
    this.status = 'destroy'
    this.recorder.destroy()
    if (this.playingUrl) {
      URL.revokeObjectURL(this.playingUrl)
      this.playingUrl = ''
    }
    this.lastWav = null
  }

  /** 送评；录音上传与结果上报都不拖住评测本身 */
  async __process(wav, durationMs) {
    try {
      const { credential, result: evaluation } = await assess({
        wav,
        refText: this.refText,
        category: this.category,
      })
      if (this.destroyed) return null

      // 录音上传与结果上报共用一个 id，后端两边能对上
      const clientRecordId = uuid()
      let uploaded = { mp3Url: '', xmlUrl: '' }
      try {
        uploaded = await uploadRecording(wav, {
          sid: clientRecordId,
          resultJson: JSON.stringify(evaluation.raw || {}),
        })
      } catch (e) {
        // 只影响回放链接与存档，不影响出分
        console.warn('[speech] 录音上传失败', e)
      }
      if (this.destroyed) return null

      // 上报不阻塞 UI，并且内部永不 reject
      reportEvaluation({
        clientRecordId,
        vendor: evaluation.vendor,
        issueId: credential && credential.issue_id,
        bizSource: this.bizContext.bizSource || 'exam',
        bizId: this.bizContext.bizId,
        questionId: this.bizContext.questionId,
        attempt: this.bizContext.attempt,
        refText: this.refText,
        audioUrl: uploaded.mp3Url,
        durationMs,
        result: evaluation,
      })

      const payload = {
        evaluation,
        mp3Url: uploaded.mp3Url,
        xmlUrl: uploaded.xmlUrl,
        durationMs,
        clientRecordId,
      }
      this.status = 'ready'
      this.evaluate_callback && this.evaluate_callback(payload)
      return payload
    } catch (e) {
      this.status = 'ready'
      const err = toSpeechError(e)
      this.__emitError(err, 'processing')
      return null
    }
  }

  /**
   * 预取凭证只为读出 vendor 对应的录音时长上限。取过一次就不再取——
   * 之后 assess() 走同一份缓存，不会重复签发。
   */
  async __prefetchCredential() {
    const supportedVendors = evalSupportedVendors()
    const key = supportedVendors.join(',')
    // 测试环境的厂商开关切换后 supported_vendors 会变，这时要重新取一次以更新时长上限
    if (this.credentialResolved && this.credentialKey === key) return
    const credential = await getCredential({ capability: 'eval', supportedVendors })
    this.maxDurationMs = maxDurationMsFor(credential.vendor)
    this.credentialResolved = true
    this.credentialKey = key
  }

  __emitError(err, phase) {
    if (this.destroyed) return
    this.error_callback && this.error_callback(err, phase)
  }
}
